import mediasoupClient from '../mediasoup.esm.js';
import { types } from 'mediasoup-client';
import { SocketEvent } from '../base/socket/event';

export enum MediaType {
  audio = 'audioType',
  video = 'videoType',
  screen = 'screenType',
}

export enum CallbackEvent {
  exitRoom = 'exitRoom',
  startAudio = 'startAudio',
  stopAudio = 'stopAudio',
  startVideo = 'startVideo',
  stopVideo = 'stopVideo',
  startScreen = 'startScreen',
  stopScreen = 'stopScreen',
  startConsumer = 'startConsumer',
  stopConsumer = 'stopConsumer',
}

type ProducerInfo = { producerId: string, peerId: string, userId: string };

type ProducerAppData = {
  requestId: string;
  type: MediaType;
  targetUserIds?: string[];
}

type ConsumerAppData = {
  producerPeerId: string;
  producerUserId: string;
}

export class RoomClient {
  roomId: string | null = null;
  isOpen = false;
  routerRtpCapabilities: types.RtpCapabilities | undefined;
  device: types.Device | undefined;
  producerTransport: types.Transport | undefined;
  consumerTransport: types.Transport | undefined;
  consumers = new Map<string, types.Consumer<ConsumerAppData>>();
  producers = new Map<string, types.Producer<ProducerAppData>>();

  constructor(
    public request: <T>(event: string, data?: any) => Promise<T>,
    public socketOn: (event: string, listener: (data?: any) => void) => void,
    public socketOff: (event: string, listener?: (data?: any) => void) => void,
    public callback: (event: CallbackEvent, data?: any) => void,
  ) {}

  async joinRoom(roomId: string) {
    this.roomId = roomId;

    //#region create room if not exists
    try {
      const response = await this.request(SocketEvent.MediasoupCreateRoom, { roomId });
      console.log('Create room response: ', response);
    } catch (error) {
      console.error('Create room error: ', error);
    }
    //#endregion

    //#region then join room
    try {
      const response = await this.request(SocketEvent.MediasoupJoin, { roomId });
      console.log('Join room response: ', response);
    } catch (error) {
      console.error('Join room error: ', error);
      throw error;
    }
    //#endregion

    //#region then get rtp capabilities
    try {
      this.routerRtpCapabilities = await this.request<types.RtpCapabilities>(SocketEvent.MediasoupGetRouterRtpCapabilities, { roomId });
      console.log('MediasoupGetRouterRtpCapabilities response: ', this.routerRtpCapabilities);
    } catch (error) {
      console.error('MediasoupGetRouterRtpCapabilities error: ', error);
      throw error;
    }
    //#endregion

    //#region load device with routerRtpCapabilities
    try {
      this.device = new mediasoupClient.Device();
      await this.device?.load({ routerRtpCapabilities: this.routerRtpCapabilities });
      console.log('device loaded:', this.device);
    } catch (error: any) {
      if (error.name === 'UnsupportedError') {
        console.error('Not supported', error, this.routerRtpCapabilities);
      } else {
        console.error('new/load Device ERROR', error);
      }
      throw error;
    }
    //#endregion

    // then init transports

    //#region init producerTransport
    await this.createProducerTransport();
    //#endregion

    //#region init consumerTransport
    await this.createConsumerTransport();
    //#endregion

    //#region then init socket listeners for this room
    this.socketOn(
      SocketEvent.MediasoupConsumerClosed,
      this.handleSocketConsumerClosed,
    );
    this.socketOn(
      SocketEvent.MediasoupNewProducers,
      this.handleSocketNewProducers,
    );
    this.socketOn('disconnect', this.handleSocketDisconnect);
    //#endregion

    //#region then notify other participants to get producers
    try {
      await this.request(SocketEvent.MediasoupGetProducers, { roomId: this.roomId });
    } catch (error) {
      console.error('MediasoupGetProducers error: ', error);
    }
    //#endregion

    //#region finally show media streams and controls UI (mute/unmute, hide/show, etc.)
    console.log('JOINED ROOM. READY TO SEND/RECEIVE MEDIA');
    this.isOpen = true;
    //#endregion
  }

  async getRoomDetails() {
    if (!this.roomId) { 
      throw new Error('Room ID is null');
    }
    return await this.request<any>(
      SocketEvent.MediasoupGetRoomDetails,
      { roomId: this.roomId },
    );
  }

  private async createProducerTransport() {
    if (!this.device) {
      throw new Error('Device not initialized');
    }
    // Close old transport if any
    this.producerTransport?.close();
    try {
      const data = await this.request<types.TransportOptions<types.AppData>>(
        SocketEvent.MediasoupCreateWebRtcTransport, 
        { roomId: this.roomId },
      );
      this.producerTransport = this.device.createSendTransport(data);
      this.producerTransport.on(
        'connect',
        async ({ dtlsParameters }, callback, errback) => {
          try {
            await this.request(SocketEvent.MediasoupConnectTransport, { 
              roomId: this.roomId, 
              dtlsParameters, 
              transportId: data.id,
            });
            callback();
          } catch (error: any) {
            console.error(
              'SocketEvent.MediasoupConnectTransport error: ',
              error,
            );
            errback(error);
          }
        },
      );
      this.producerTransport.on(
        'produce',
        async ({ kind, rtpParameters, appData }, callback, errback) => {
          try {
            console.log('Producing kind:', kind, 'rtpParameters:', rtpParameters, 'appData:', appData);
            const { producerId } = await this.request<{ producerId: string }>(
              SocketEvent.MediasoupProduce,
              {
                roomId: this.roomId,
                producerTransportId: this.producerTransport?.id,
                kind,
                rtpParameters,
                appData,
              },
            );
            callback({
              id: producerId,
            });
          } catch (error: any) {
            console.error('SocketEvent.MediasoupProduce error: ', error);
            errback(error);
          }
        },
      );
      this.producerTransport.on('connectionstatechange', state => {
        console.log('producerTransport connectionstatechange:', state);
        switch (state) {
          case 'connecting':
            break;
          case 'connected':
            //localVideo.srcObject = stream
            break;
          case 'failed':
          case 'closed':
          case 'disconnected':
            this.createProducerTransport();
            break;
          default:
            break;
        }
      });
    } catch (error) {
      console.error(
        'producerTransport SocketEvent.MediasoupCreateWebRtcTransport ERROR',
        error,
      );
      throw error;
    }
  }

  private async createConsumerTransport() {
    if (!this.device) {
      throw new Error('Device not initialized');
    }
    // Close old transport if any
    this.consumerTransport?.close();
    try {
      const data = await this.request<types.TransportOptions<types.AppData>>(
        SocketEvent.MediasoupCreateWebRtcTransport, 
        { roomId: this.roomId },
      );
      // only one needed
      this.consumerTransport = this.device.createRecvTransport(data);
      this.consumerTransport.on(
        'connect',
        async ({ dtlsParameters }, callback, errback) => {
          try {
            await this.request(SocketEvent.MediasoupConnectTransport, {
              roomId: this.roomId,
              dtlsParameters,
              transportId: this.consumerTransport?.id,
            });
            callback();
          } catch (error: any) {
            console.error(
              'SocketEvent.MediasoupConnectTransport error: ',
              error,
            );
            errback(error);
          }
        },
      );
      this.consumerTransport.on('connectionstatechange', async state => {
        console.log('consumerTransport connectionstatechange:', state);
        switch (state) {
          case 'connecting':
            break;
          case 'connected':
            //remoteVideo.srcObject = await stream;
            //await this.request('resume');
            break;
          case 'failed':
          case 'closed':
          case 'disconnected':
            this.createConsumerTransport();
            break;
          default:
            break;
        }
      });
    } catch (error) {
      console.error(
        'consumerTransport SocketEvent.MediasoupCreateWebRtcTransport ERROR',
        error,
      );
      throw error;
    }
  }

  async produce(config: {
    requestId: string;
    type: MediaType;
    deviceId?: string;
    appData?: Partial<ProducerAppData>;
  }) {
    const { requestId, type, deviceId, appData } = config;
    let mediaConstraints: MediaStreamConstraints = {};
    let audio = false;
    let screen = false;
    switch (type) {
      case MediaType.audio:
        mediaConstraints = {
          audio: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
          },
          video: false,
        };
        audio = true;
        break;
      case MediaType.video:
        mediaConstraints = {
          audio: false,
          video: {
            width: {
              min: 640,
              ideal: 1920,
            },
            height: {
              min: 400,
              ideal: 1080,
            },
            deviceId: deviceId ? { exact: deviceId } : undefined,
            /*aspectRatio: {
                            ideal: 1.7777777778
                        }*/
          },
        };
        break;
      case MediaType.screen:
        mediaConstraints = {};
        screen = true;
        break;
      default:
        return;
    }
    if (!this.producerTransport) {
      throw new Error('Producer transport is not initialized');
    }
    if (!this.device?.canProduce('video') && !audio) {
      throw new Error('Cannot produce video');
    }
    for (const producer of Array.from(this.producers.values())) {
       if (producer.appData?.requestId === requestId) {
        throw new Error('Producer already exists for this request ' + requestId);
      }
    }
    let stream: MediaStream;
    try {
      stream = screen ? await navigator.mediaDevices.getDisplayMedia() : await navigator.mediaDevices.getUserMedia(mediaConstraints);

      const track = audio
        ? stream.getAudioTracks()[0]
        : stream.getVideoTracks()[0];
      console.log('produce stream track', track);

      const params: types.ProducerOptions<ProducerAppData> = {
        track,
        appData: { ...appData, requestId, type },
      };
      if (!audio && !screen) {
        params.encodings = [
          {
            rid: 'r0',
            maxBitrate: 100000,
            //scaleResolutionDownBy: 10.0,
            scalabilityMode: 'S1T3',
          },
          {
            rid: 'r1',
            maxBitrate: 300000,
            scalabilityMode: 'S1T3',
          },
          {
            rid: 'r2',
            maxBitrate: 900000,
            scalabilityMode: 'S1T3',
          },
        ];
        params.codecOptions = {
          videoGoogleStartBitrate: 1000,
        };
      }

      const producer = await this.producerTransport.produce(params);

      console.log('Producer', producer);

      this.producers.set(producer.id, producer);

      producer.on('trackended', () => {
        console.log('Producer track ended');
        this.closeProducer(requestId);
      });

      producer.on('transportclose', () => {
        console.log('Producer transport close');
        if (!audio) {
          this.callback(CallbackEvent.stopVideo);
        }
        this.producers.delete(producer.id);
      });

      producer.on('@close', () => {
        console.log('Producer @close');
        if (!audio) {
          this.callback(CallbackEvent.stopVideo);
        }
        this.producers.delete(producer.id);
      });

      switch (type) {
        case MediaType.audio:
          this.callback(CallbackEvent.startAudio, { stream, producer });
          break;
        case MediaType.video:
          this.callback(CallbackEvent.startVideo, { stream, producer });
          break;
        case MediaType.screen:
          this.callback(CallbackEvent.startScreen);
          break;
        default:
          return;
      }
    } catch (err) {
      console.error('Produce error:', err);
      throw err;
    }
  }

  async closeProducer(requestId: string) {
    // get producer by requestId
    const producer = Array.from(this.producers.values()).find(producer => producer.appData?.requestId === requestId); 
    if (!producer) {
      throw new Error('There is no producer for this request ' + requestId);
    }

    const producerId = producer.id;
    const type = producer.appData?.type;

    console.log('Close producer', producerId, type);

    this.request(SocketEvent.MediasoupProducerClosed, { roomId: this.roomId, producerId });

    this.producers.get(producerId)?.close();
    this.producers.delete(producerId);

    switch (type) {
      case MediaType.audio:
        this.callback(CallbackEvent.stopAudio);
        break;
      case MediaType.video:
        this.callback(CallbackEvent.stopVideo);
        break;
      case MediaType.screen:
        this.callback(CallbackEvent.stopScreen);
        break;
      default:
        return;
    }
  }

  private handleSocketConsumerClosed = async ({ consumerId }: { consumerId: string }) => {
    console.log('Closing consumer:', consumerId);
    this.removeConsumer(consumerId);
  };

  private handleSocketNewProducers = async (
    data: ProducerInfo[],
  ) => {
    console.log('New producers', data);
    this.enqueue(async () => {
      const currentConsumers = Array.from(this.consumers.values());
      for (let { producerId, peerId, userId } of data) {
        if (currentConsumers.find(consumer => consumer.producerId === producerId)) {
          // already consuming
          continue;
        }
        try {
          await this.consume(producerId);
        } catch (error) {
          console.error('Consume error:', producerId, error);
        }
      }
    });
  };

  private handleSocketDisconnect = () => {
    console.log('Socket disconnected');
    this.exit(true);
  };

  private async consume(producerId: string) {
    const { consumer, stream, kind } = await this.getConsumeStream(producerId);
    this.consumers.set(consumer.id, consumer);

    this.callback(CallbackEvent.startConsumer, { consumer, stream, kind });

    consumer.on('trackended', () => {
      this.removeConsumer(consumer.id);
    });

    consumer.on('transportclose', () => {
      this.removeConsumer(consumer.id);
    });
  }

  private async getConsumeStream(producerId: string) {
    if (!this.device || !this.consumerTransport) {
      throw new Error('Device or consumer transport not initialized');
    }
    const { rtpCapabilities } = this.device;
    const data = await this.request<types.ConsumerOptions<ConsumerAppData>>(
      SocketEvent.MediasoupConsume,
      {
        roomId: this.roomId,
        rtpCapabilities,
        consumerTransportId: this.consumerTransport.id, // might be
        producerId,
      },
    );
    const { id, kind, rtpParameters, appData } = data;

    const consumer = await this.consumerTransport.consume({
      id,
      producerId,
      kind,
      rtpParameters,
      appData,
    });

    const stream = new MediaStream();
    stream.addTrack(consumer.track);

    return {
      consumer,
      stream,
      kind,
    };
  }

  private removeConsumer(consumerId: string) {
    const consumer = this.consumers.get(consumerId);
    if (consumer) {
      this.callback(CallbackEvent.stopConsumer, {
        kind: consumer.kind,
        consumerId: consumer.id,
      });
    }

    this.consumers.delete(consumerId);
  }

  exit = (offline = false) => {
    const clean = () => {
      this.isOpen = false;
      this.consumers.forEach(consumer => consumer.close());
      this.consumers.clear();
      this.producers.forEach(producer => producer.close());
      this.producers.clear();
      this.consumerTransport?.removeAllListeners();
      this.consumerTransport?.close();
      this.producerTransport?.removeAllListeners();
      this.producerTransport?.close();
      this.socketOff('disconnect', this.handleSocketDisconnect);
      this.socketOff(
        SocketEvent.MediasoupNewProducers,
        this.handleSocketNewProducers,
      );
      this.socketOff(
        SocketEvent.MediasoupConsumerClosed,
        this.handleSocketConsumerClosed,
      );
    };

    if (!offline) {
      this.request(SocketEvent.MediasoupExitRoom, { roomId: this.roomId })
        .catch(console.error)
        .finally(() => clean());
    } else {
      clean();
    }

    this.callback(CallbackEvent.exitRoom);
  };

  private queue = Promise.resolve();
  private enqueue(task: () => void | PromiseLike<void>) {
    this.queue = this.queue.then(task).catch(() => {});
  }
}
